import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Drawer } from '@/ui/Drawer';
import { Field, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { cn } from '@/lib/cn';
import { Filtros, type Chip } from './Filtros';
import { Aviso } from './MisSolicitudes';
import { EstadoBadge, ValorDeFormulario, Adjuntos } from './piezas';
import {
  esCerrada, esperaDesde, useBandeja, useDecidir, useDetalle, usePorAprobar,
  type FiltroBandeja, type FilaDeSolicitud,
} from './api';

/* ── Por aprobar ───────────────────────────────────────────────────────── */

/**
 * Lo que espera MI decisión.
 *
 * No es una pestaña de gestión: la ve cualquier colaborador que tenga algo que
 * firmar, aunque no sea staff. Es la que hace que un jefe sin rol de
 * administración pueda aprobar.
 */
export function PorAprobar() {
  const consulta = usePorAprobar();
  const [revisando, setRevisando] = useState<number | null>(null);
  const items = consulta.data ?? [];

  if (consulta.isError) {
    return <EstadoError err={consulta.error} que="las solicitudes por aprobar"
                        onReintentar={() => void consulta.refetch()} />;
  }
  if (consulta.isLoading) {
    return <Card><div className="flex flex-col gap-2 p-4">
      {[0, 1].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
    </div></Card>;
  }
  if (!items.length) {
    return <EstadoVacio icono="bi-check-circle" titulo="No tienes solicitudes pendientes de aprobar"
                        descripcion="Cuando alguien radique algo que tengas que decidir, aparecerá aquí." />;
  }

  // La cabecera que pide el tablero: cuántas esperan y cuánto lleva la peor. Es
  // el dato que decide si esto se mira ahora o después, y no estaba en ninguna
  // parte — había que abrir una por una para saberlo.
  const esperas = items.map((r) => esperaDesde(r.created_at)).filter(Boolean);
  const peor = esperas.length ? Math.max(...esperas.map((e) => e!.dias)) : 0;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-3 text-ink-2">
        <strong>{items.length}</strong>{' '}
        {items.length === 1 ? 'espera tu decisión' : 'esperan tu decisión'}
        {peor > 0 && <> · la más antigua lleva <strong>{peor} {peor === 1 ? 'día' : 'días'}</strong></>}
      </p>

      <Tabla
        filas={items}
        rotulo="Solicitudes que esperan tu decisión"
        columnaSolicitante
        columnaEspera
        accion={(r) => (
          <Button size="sm" onClick={() => setRevisando(r.id)}>
            <i className="bi bi-check2" aria-hidden="true" /> Revisar
          </Button>
        )}
      />

      <PanelDeRevision id={revisando} onCerrar={() => setRevisando(null)} />
    </div>
  );
}

/* ── Bandeja (gestión) ─────────────────────────────────────────────────── */

const TAM = 50;
const CHIPS: Chip[] = [
  { clave: 'abiertas', texto: 'En curso', icono: 'bi-hourglass-split' },
  { clave: 'cerradas', texto: 'Cerradas', icono: 'bi-check2-all' },
  { clave: 'todas', texto: 'Todas', icono: 'bi-list-ul' },
];

export function Bandeja() {
  const [filtro, setFiltro] = useState<FiltroBandeja>({ estado: 'abiertas', q: '' });
  const [texto, setTexto] = useState('');
  const [limite, setLimite] = useState(TAM);
  const [revisando, setRevisando] = useState<number | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setFiltro((f) => (f.q === texto.trim() ? f : { ...f, q: texto.trim() }));
      setLimite(TAM);
    }, 350);
    return () => window.clearTimeout(t);
  }, [texto]);

  const consulta = useBandeja(filtro, limite);
  const filas = consulta.data?.items ?? [];
  const total = consulta.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <Filtros
        chips={CHIPS}
        activo={filtro.estado}
        onElegir={(k) => { setFiltro((f) => ({ ...f, estado: k })); setLimite(TAM); }}
        busqueda={texto}
        onBuscar={setTexto}
        etiquetaBusqueda="Buscar por radicado, tipo o solicitante"
        marcador="Radicado, tipo o solicitante…"
      />

      {consulta.isError ? (
        <EstadoError err={consulta.error} que="la bandeja de solicitudes"
                     onReintentar={() => void consulta.refetch()} />
      ) : consulta.isLoading ? (
        <Card><div className="flex flex-col gap-2 p-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div></Card>
      ) : filas.length === 0 ? (
        <EstadoVacio
          icono="bi-inbox"
          titulo={filtro.q ? `Ningún resultado para «${filtro.q}»`
            : filtro.estado === 'abiertas' ? 'No hay solicitudes en curso' : 'Sin solicitudes'}
        />
      ) : (
        <>
          <Tabla
            filas={filas}
            rotulo="Bandeja de solicitudes"
            columnaSolicitante
            accion={(r) => {
              // La fila propia dice «Ver» y no «Revisar»: el verbo tiene que
              // coincidir con lo que se puede hacer al pulsarlo.
              const mia = r.applicant_id != null && r.applicant_id === Auth.getUser()?.id;
              return (
                <Button size="sm" variant="ghost" onClick={() => setRevisando(r.id)}>
                  <i className={mia ? 'bi bi-eye' : 'bi bi-pencil-square'} aria-hidden="true" />
                  {mia ? ' Ver' : ' Revisar'}
                </Button>
              );
            }}
          />
          {filas.length < total && (
            <div className="flex justify-center">
              <Button variant="secondary" size="sm" loading={consulta.isFetching}
                      onClick={() => setLimite((l) => l + TAM)}>
                Mostrar más ({filas.length} de {total})
              </Button>
            </div>
          )}
        </>
      )}

      <PanelDeRevision id={revisando} onCerrar={() => setRevisando(null)} />
    </div>
  );
}

/* ── La tabla que comparten las dos ────────────────────────────────────── */

function Tabla({
  filas, rotulo, columnaSolicitante, columnaEspera, accion,
}: {
  filas: FilaDeSolicitud[];
  /** Nombre de la tabla. Sin él se anuncia como «tabla» a secas, y esta
   *  pantalla tiene dos con las mismas columnas. */
  rotulo: string;
  columnaSolicitante?: boolean;
  columnaEspera?: boolean;
  accion: (r: FilaDeSolicitud) => React.ReactNode;
}) {
  return (
    <Card className="overflow-x-auto">
      <table aria-label={rotulo} className="w-full min-w-[48rem] border-collapse text-3">
        <thead>
          <tr className="border-b border-line text-left text-2 uppercase tracking-wide text-muted">
            <th scope="col" className="px-4 py-2.5 font-semibold">Radicado</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Tipo</th>
            {columnaSolicitante && <th scope="col" className="px-4 py-2.5 font-semibold">Solicitante</th>}
            {columnaEspera && <th scope="col" className="px-4 py-2.5 font-semibold">Espera</th>}
            <th scope="col" className="px-4 py-2.5 font-semibold">Estado</th>
            <th scope="col" className="px-4 py-2.5 font-semibold">Fecha</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {filas.map((r) => {
            const e = esperaDesde(r.created_at);
            return (
              <tr key={r.id} className="border-b border-line last:border-0">
                <td className="whitespace-nowrap px-4 py-3 font-mono text-2 text-ink-2">{r.radicado}</td>
                <td className="whitespace-nowrap px-4 py-3 font-medium text-ink">{r.type_name}</td>
                {columnaSolicitante && <td className="whitespace-nowrap px-4 py-3 text-ink-2">{r.applicant}</td>}
                {columnaEspera && (
                  <td className="px-4 py-3">
                    {e ? (
                      <span
                        title={`Radicada el ${Utils.fmtDateOnly(r.created_at)}`}
                        className={cn('text-2 font-semibold',
                          e.nivel === 'malo' ? 'text-danger-ink'
                            : e.nivel === 'alerta' ? 'text-warn-ink' : 'text-muted')}
                      >
                        {e.etiqueta}
                      </span>
                    ) : '—'}
                  </td>
                )}
                <td className="px-4 py-3"><EstadoBadge estado={r.status} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-2 text-muted">{Utils.fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-right">{accion(r)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/* ── El panel de revisión ──────────────────────────────────────────────── */

const DECISION_TXT: Record<string, string> = {
  aprobado: 'aprobada', rechazado: 'rechazada',
  devuelto: 'devuelta al solicitante para corrección',
};

/**
 * Revisar y decidir.
 *
 * Es un panel y no un diálogo porque hay que **leer** antes de decidir: los
 * datos del formulario, los choques de fechas del solicitante y quién más del
 * área estará fuera esos días. Un diálogo dice «olvídate de lo de detrás», y
 * aquí lo de detrás es la cola que se está despachando.
 *
 * «Devolver» va entre rechazar y aprobar porque es la salida intermedia: ni se
 * autoriza ni se niega, se pide una corrección.
 */
function PanelDeRevision({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const consulta = useDetalle(id);
  const decidir = useDecidir();
  const [nota, setNota] = useState('');
  const [errorNota, setErrorNota] = useState<string | undefined>();

  useEffect(() => { setNota(''); setErrorNota(undefined); }, [id]);

  const r = consulta.data;
  const cerrada = esCerrada(r?.status);
  const faltan = (r?.soportes_faltantes ?? []).length;
  const paso = (r?.flow ?? [])[r?.current_step ?? 0];

  // Nadie decide sobre lo suyo, **ni siquiera un admin**: `approve_request`
  // responde 403 antes que cualquier otra comprobación, y a propósito —el admin
  // existe para destrabar lo de otros, no para autorizarse a sí mismo—.
  //
  // «Por aprobar» nunca la enseña, porque `pending-for-me` ya filtra por
  // `_can_approve`. La **Bandeja** sí: le muestra a gestión TODAS las
  // solicitudes, incluidas las suyas. Ahí los tres botones invitaban a una
  // decisión que siempre terminaba en un toast de error.
  const esMia = r?.applicant_id != null && r.applicant_id === Auth.getUser()?.id;

  // El servidor rechaza aprobar sin los soportes obligatorios (400): en una
  // incapacidad o una licencia de luto es requisito legal. El panel ya lo decía
  // con palabras, pero dejaba el botón invitando a intentarlo y el aprobador
  // solo se enteraba al chocar. Rechazar y devolver siguen habilitados: son las
  // salidas que SÍ tiene.
  const motivoBloqueo = faltan === 1
    ? 'Falta un soporte obligatorio. Puedes devolverla para que lo adjunte, o rechazarla.'
    : `Faltan ${faltan} soportes obligatorios. Puedes devolverla para que los adjunte, o rechazarla.`;

  async function decide(decision: 'aprobado' | 'rechazado' | 'devuelto') {
    // Se comprueba aquí además de en el servidor para no perder de vista el
    // campo: el aviso aparece junto al recuadro donde hay que escribir.
    if (decision === 'devuelto' && !nota.trim()) {
      setErrorNota('Escribe qué debe corregir el solicitante.');
      document.getElementById('sol-approve-note')?.focus();
      return;
    }
    try {
      const res = await decidir.mutateAsync({ id: id!, decision, note: nota });
      Utils.toast(`Solicitud ${DECISION_TXT[decision] || res?.status || ''}`, 'success');
      onCerrar();
    } catch (err) {
      Utils.toast(err instanceof Error ? err.message : 'No se pudo registrar la decisión', 'error');
    }
  }

  return (
    <Drawer
      abierto={id != null}
      onCambio={(v) => { if (!v) onCerrar(); }}
      // El título dice lo que se puede hacer: sobre la propia no se revisa nada.
      titulo={r ? `${esMia ? 'Solicitud' : 'Revisar'} ${r.radicado}` : 'Revisar'}
      descripcion={r ? [r.type_name, r.applicant].filter(Boolean).join(' · ') : undefined}
      ancho="lg"
      pie={
        cerrada || !r || esMia ? (
          <Button variant="secondary" onClick={onCerrar}>Cerrar</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
            <Button variant="destructive" loading={decidir.isPending}
                    onClick={() => void decide('rechazado')}>
              Rechazar
            </Button>
            <Button variant="secondary" loading={decidir.isPending}
                    onClick={() => void decide('devuelto')}>
              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Devolver
            </Button>
            <Button
              loading={decidir.isPending}
              disabled={faltan > 0}
              razonDeshabilitado={faltan > 0 ? motivoBloqueo : undefined}
              onClick={() => void decide('aprobado')}
            >
              Aprobar paso
            </Button>
          </>
        )
      }
    >
      {consulta.isLoading && (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}
      {consulta.isError && (
        <EstadoError err={consulta.error} que="esta solicitud"
                     onReintentar={() => void consulta.refetch()} />
      )}

      {r && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-3">
            <EstadoBadge estado={r.status} />
            {paso && !cerrada && (
              <span className="text-2 text-muted">Paso actual: <strong className="text-ink-2">{paso.label}</strong></span>
            )}
            {r.current_approver && !cerrada && (
              <span className="flex items-center gap-1.5 text-2 text-muted">
                <i className="bi bi-person-check" aria-hidden="true" />
                Aprobador: <strong className="text-ink-2">{r.current_approver.approver_name}</strong>
              </span>
            )}
          </div>

          <div className="rounded-sm border border-line bg-surface px-4 py-3">
            {(r.fields ?? []).filter((f) => f.type !== 'file').map((f) => (
              <div key={f.key} className="py-1">
                <span className="text-2 uppercase tracking-wide text-muted">{f.label}</span>
                <p className="text-3 text-ink"><ValorDeFormulario valor={r.form_data?.[f.key]} /></p>
              </div>
            ))}
            {!(r.fields ?? []).some((f) => f.type !== 'file') && (
              <p className="text-3 text-muted">—</p>
            )}
          </div>

          <Adjuntos lista={r.attachments} />
          <Cobertura r={r} />

          {esMia ? (
            <Aviso tono="info" icono="bi-person-exclamation"
                   titulo="Esta solicitud es tuya, así que la decide otra persona.">
              <p className="mt-1 text-2 leading-relaxed">
                Nadie aprueba lo suyo, ni siquiera con rol de administración: la revisa
                quien corresponda al paso actual. Aquí puedes consultarla, y desde
                «Mis solicitudes» cancelarla si ya no la necesitas.
              </p>
            </Aviso>
          ) : cerrada ? (
            <Aviso tono="info" icono="bi-info-circle" titulo="Esta solicitud ya está cerrada." />
          ) : (
            <Field
              label="Nota / observación"
              ayuda="Obligatoria si devuelves la solicitud."
              error={errorNota}
              id="sol-approve-note"
            >
              <Textarea
                rows={2}
                value={nota}
                onChange={(e) => { setNota(e.target.value); setErrorNota(undefined); }}
              />
            </Field>
          )}
        </div>
      )}
    </Drawer>
  );
}

/** Contexto de fechas para quien decide: choques del propio solicitante y quién
 *  más del área estará fuera esos días. Es informativo, no bloquea: cuántas
 *  ausencias simultáneas se toleran depende del área, no del sistema. */
function Cobertura({ r }: { r: NonNullable<ReturnType<typeof useDetalle>['data']> }) {
  const faltan = r.soportes_faltantes ?? [];
  const propios = r.solapes_propios ?? [];
  const equipo = r.equipo_ausente ?? [];
  if (!faltan.length && !propios.length && !equipo.length) return null;

  return (
    <div className="flex flex-col gap-3">
      {faltan.length > 0 && (
        <Aviso tono="danger" icono="bi-paperclip"
               titulo={faltan.length === 1 ? 'Falta un soporte obligatorio'
                 : `Faltan ${faltan.length} soportes obligatorios`}>
          <ul className="mt-1.5 list-disc pl-5 text-2">
            {faltan.map((x) => <li key={x.key}>{x.label}</li>)}
          </ul>
          <p className="mt-1.5 text-2 leading-relaxed">
            No se puede aprobar sin ellos. Sí se puede rechazar. El solicitante ya puede subirlos
            desde su pantalla, y te llegará un correo cuando lo haga.
          </p>
        </Aviso>
      )}

      {propios.length > 0 && (
        <Aviso tono="warn" icono="bi-exclamation-triangle"
               titulo={propios.length === 1
                 ? 'El solicitante ya tiene otra solicitud en esas fechas'
                 : `El solicitante ya tiene otras ${propios.length} solicitudes en esas fechas`}>
          <ul className="mt-1.5 list-disc pl-5 text-2 leading-relaxed">
            {propios.map((p, i) => (
              <li key={i}>{p.tipo} · {p.inicio} a {p.fin} <em>({p.estado})</em></li>
            ))}
          </ul>
        </Aviso>
      )}

      {equipo.length > 0 && (
        <Aviso tono="info" icono="bi-people"
               titulo={equipo.length === 1
                 ? 'Otra persona del área también estará fuera'
                 : `Otras ${equipo.length} personas del área también estarán fuera`}>
          <ul className="mt-1.5 list-disc pl-5 text-2 leading-relaxed">
            {equipo.map((e, i) => (
              <li key={i}>{e.colaborador} — {e.tipo} · {e.inicio} a {e.fin} <em>({e.estado})</em></li>
            ))}
          </ul>
        </Aviso>
      )}
    </div>
  );
}
